import { fork, put, call, take, select, race, delay, all } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import invoke from 'lodash/invoke';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import { refreshToken } from 'yoda-interfaces/lib/Account/AccountApi';
import User from 'yoda-site-components/lib/helpers/User/User';
import { REFRESH_TOKEN, ACCOUNT_ID } from 'yoda-site-components/lib/common/Constants';
import {
    IOVATION_SCRIPT_LOAD_ERROR,
    IOVATION_SCRIPT_LOAD_SUCCESS,
} from 'yoda-site-components/lib/actionTypes/KeepMeLoggedInActionTypes';
import { selectPreferences } from 'yoda-site-components/lib/selectors/ContextSelector';
import CoreConstants from 'yoda-core-components/lib/common/Constants';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import { selectKeepMeLoggedInThrottleFF } from 'yoda-site-components/lib/selectors/KeepMeLoggedInSelector';
import * as TokenActionTypes from '../actionTypes/TokenActionTypes';

const { DP_KEEP_ME_LOGGED } = CoreConstants;

export const isMobileWebsite = (loc) => {
    if (
        loc === 'm.jcpenney.com' ||
        loc === 'm-dt-test1.jcpenney.com' ||
        loc === 'm-dt-test2.jcpenney.com' ||
        loc === 'm-dt-test3.jcpenney.com' ||
        loc === 'm-dt-test4.jcpenney.com'
    ) {
        return true;
    }
    return false;
};

function getBBId() {
    return !__SERVER__ && get(invoke(window, 'IGLOO.getBlackbox'), 'blackbox');
}

function* refreshTokenProviderSaga(action) {
    try {
        const accountId = TokenProvider.get(ACCOUNT_ID);
        let customHeaders = {};
        const enableKeepMeLoggedIn = yield select(selectKeepMeLoggedInThrottleFF);
        const keepMeLoggedInSelectedInSession = TokenProvider.get(DP_KEEP_ME_LOGGED) === '1';
        if (enableKeepMeLoggedIn && User.isUserLoggedIn(true) && keepMeLoggedInSelectedInSession) {
            let bbId = getBBId();
            if (!bbId) {
                // eslint-disable-next-line no-console
                console.warn('Iovation Script is loaded yet...');
                const preferences = yield select(selectPreferences);
                const { type } = yield race([
                    take([IOVATION_SCRIPT_LOAD_ERROR, IOVATION_SCRIPT_LOAD_SUCCESS]),
                    // this race condition added so no call blocks
                    delay(get(preferences, 'iovationScriptLoadTimeOut', 2000)),
                ]);
                if (!type || type === IOVATION_SCRIPT_LOAD_ERROR) {
                    // eslint-disable-next-line no-console
                    console.warn('Iovation script failed or timed out: -> ', type);
                } else {
                    bbId = getBBId();
                }
            }
            customHeaders = {
                ...(bbId && { 'X-BLACKBOX': bbId }),
            };
        }

        const response = yield call(
            refreshToken,
            TokenProvider.get(REFRESH_TOKEN),
            accountId,
            customHeaders
        );
        if (!isEmpty(response) && response.status !== null && response.status === 200) {
            TokenProvider.storeTokens(response, 'body');

            yield put({
                type: 'GET_REFRESH_TOKEN_REQUEST_SUCCESS_ACCOUNT',
                payload: action.payload,
            });
        } else {
            window.location.assign('/sessiontimeout?timeout=true');
        }
    } catch (error) {
        yield put({ type: 'GET_REFRESH_TOKEN_REQUEST_ERROR_ACCOUNT', error });
    }
}

const watchRefreshTokenProviderRequest = function* watchRefreshTokenProviderRequest() {
    // NOSONAR
    const actions = [];
    let waitingResponse = false;
    const {
        GET_REFRESH_TOKEN_REQUEST_ACCOUNT,
        GET_REFRESH_TOKEN_REQUEST_SUCCESS_ACCOUNT,
        GET_REFRESH_TOKEN_REQUEST_ERROR_ACCOUNT,
    } = TokenActionTypes;
    while (true) {
        // NOSONAR
        const action = yield take([
            GET_REFRESH_TOKEN_REQUEST_ACCOUNT,
            GET_REFRESH_TOKEN_REQUEST_SUCCESS_ACCOUNT,
            GET_REFRESH_TOKEN_REQUEST_ERROR_ACCOUNT,
        ]);
        if (action.type === GET_REFRESH_TOKEN_REQUEST_ACCOUNT) {
            if (!waitingResponse) {
                // Flag of refresh token already going on
                waitingResponse = true;
                yield fork(refreshTokenProviderSaga, action);
                actions.push(action.payload);
            } else {
                actions.push(action.payload);
            }
        }
        if (action.type === GET_REFRESH_TOKEN_REQUEST_SUCCESS_ACCOUNT) {
            waitingResponse = false;
            const actionProm = [];
            while (actions.length > 0) {
                actionProm.push(put(actions.pop()));
            }
            yield all(actionProm);
        }
        if (action.type === GET_REFRESH_TOKEN_REQUEST_ERROR_ACCOUNT) {
            waitingResponse = false;
            while (actions.length > 0) {
                actions.pop();
            }
            getBrowserHistory()('/sessionTimeout');
        }
    }
    // yield takeLatest('GET_REFRESH_TOKEN_REQUEST_ACCOUNT_ONCE', refreshTokenProviderSaga);
};
watchRefreshTokenProviderRequest.sagaName = 'watchRefreshTokenProviderRequest';

export default watchRefreshTokenProviderRequest;
